/**
 * Tax card (verokortti) check: does the withholding rate on the card match the tax this salary
 * actually attracts? A Finnish tax card carries a base rate up to an annual income limit and an
 * additional rate above it. Withholding covers taxes only (state, municipal, church, Yle, the
 * medical-care contribution); pension and unemployment contributions are deducted separately,
 * so the comparison is against the calculator's taxes, not its contributions.
 */
export interface TaxCardInput {
  grossAnnual: number;
  /** Taxes the calculator computes for the year (NetSalaryResult.taxes.total). */
  taxesAnnual: number;
  /** Base withholding rate on the card (perusprosentti), %. */
  baseRatePct: number;
  /** Annual income limit on the card (tuloraja), EUR. Omitted: the base rate applies to all income. */
  incomeLimitAnnual?: number;
  /** Additional rate above the limit (lisäprosentti), %. */
  extraRatePct?: number;
}

export type TaxCardVerdict = 'refund' | 'backTax' | 'onTarget';

export interface TaxCardCheck {
  withheldAnnual: number;
  actualTaxAnnual: number;
  /** Withheld minus actual: positive means a refund is coming, negative means back taxes. */
  differenceAnnual: number;
  /** The rate that would have hit the actual tax exactly, %. */
  impliedRatePct: number;
  verdict: TaxCardVerdict;
}

/** Below this the card is "about right": the tax office's own rounding is coarser than this. */
const ON_TARGET_TOLERANCE_EUR = 60;

export function checkTaxCard(i: TaxCardInput): TaxCardCheck {
  const gross = Math.max(0, i.grossAnnual);
  const limit = i.incomeLimitAnnual !== undefined && i.incomeLimitAnnual > 0 ? i.incomeLimitAnnual : undefined;
  const base = Math.max(0, i.baseRatePct) / 100;
  const extra = Math.max(0, i.extraRatePct ?? i.baseRatePct) / 100;
  const withinLimit = limit === undefined ? gross : Math.min(gross, limit);
  const aboveLimit = limit === undefined ? 0 : Math.max(0, gross - limit);
  const withheldAnnual = withinLimit * base + aboveLimit * extra;
  const actualTaxAnnual = Math.max(0, i.taxesAnnual);
  const differenceAnnual = withheldAnnual - actualTaxAnnual;
  return {
    withheldAnnual,
    actualTaxAnnual,
    differenceAnnual,
    impliedRatePct: gross > 0 ? (actualTaxAnnual / gross) * 100 : 0,
    verdict: Math.abs(differenceAnnual) <= ON_TARGET_TOLERANCE_EUR ? 'onTarget' : differenceAnnual > 0 ? 'refund' : 'backTax',
  };
}
